import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NsKey } from 'enums/NsKey';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { GenericLoading } from 'components/ui/Loader';
import { usePanel } from 'hooks/usePanel';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'enums/sizes';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import {
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { CollaboratorDetailModal } from './CollaboratorDetailModal';
import { AddMemberCard, CollaboratorCard } from './CollaboratorCard';

import {
  AccessMember,
  getPersonneMoraleMembers
} from 'features/access/api/personneMoraleAccessApi';
import { AddCollaboratorForm } from 'features/access/components/team/form/AddCollaboratorForm';
import { DeclareGerantPanel } from 'features/access/components/team/DeclareGerantPanel';
import {
  buildBaseMemberMenuItems,
  useMemberTeamActions
} from 'features/access/components/team/memberTeamShared';

interface MembersManagerProps {
  /** PM dont on gère les accès (pour une plateforme : sa PM corporate). */
  personneMoraleId: string;
  /** Titre de la section (compteur ajouté) — à omettre sous un onglet qui
      porte déjà le libellé et le badge. */
  title?: string;
  /** Description affichée au-dessus des cartes. */
  titleDescription?: string;
  onMembersCountChange?: (count: number) => void;
}

/**
 * Gestion générique des membres ayant accès au compte d'une personne morale :
 * grille de cartes collaborateur, ajout (existant ou créé), gérant, promotion
 * principal, retrait. Réutilisé par le détail PM, le détail d'une plateforme
 * (membres du compte corporate) et la consultation rapide d'un intermédiaire.
 */
export const CollaboratorsManager: React.FC<MembersManagerProps> = ({
  personneMoraleId,
  title,
  titleDescription,
  onMembersCountChange
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);
  const { openPanel } = usePanel();
  const { openDialog } = useDialog();
  const [members, setMembers] = useState<AccessMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<AccessMember | null>(
    null
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPersonneMoraleMembers(personneMoraleId);
      setMembers(data.collaborators);
      onMembersCountChange?.(data.collaborators.length);
    } catch {
      toasterService.showToast({
        message: t('members.loadError'),
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red
      });
    } finally {
      setLoading(false);
    }
  }, [personneMoraleId, onMembersCountChange, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openAddForm = useCallback(() => {
    openPanel(
      { title: t('members.addCollaborator'), size: Sizes.lg },
      <AddCollaboratorForm
        personneMoraleId={personneMoraleId}
        onSuccess={refresh}
      />
    );
  }, [personneMoraleId, openPanel, refresh, t]);

  const { handleSetOwner, handlePromote, removeMember, handleRemoveGerant } =
    useMemberTeamActions(refresh);

  const openDeclareGerant = useCallback(
    (member: AccessMember) => {
      openPanel(
        { title: t('members.declareSignatoryTitle'), size: Sizes.lg },
        <DeclareGerantPanel
          personneMoraleId={personneMoraleId}
          defaultAccessId={member.accessId}
          onSuccess={refresh}
        />
      );
    },
    [openPanel, personneMoraleId, refresh, t]
  );

  const handleRemove = useCallback(async () => {
    if (!memberToRemove) return;
    if (await removeMember(memberToRemove)) {
      setMemberToRemove(null);
    }
  }, [memberToRemove, removeMember]);

  const openMemberModal = useCallback(
    (member: AccessMember) => {
      openDialog(
        { title: t('members.collaboratorDetail'), size: Sizes.lg },
        <CollaboratorDetailModal member={member} onSuccess={refresh} />
      );
    },
    [openDialog, refresh, t]
  );

  const { createMenuItem } = useActionMenu();

  const createMenuItems = useCallback(
    (member: AccessMember) => [
      ...buildBaseMemberMenuItems(createMenuItem, member, {
        onPromote: handlePromote,
        onToggleOwner: handleSetOwner,
        onDeclareGerant: openDeclareGerant,
        onRemoveGerant: handleRemoveGerant
      }),
      createMenuItem(
        'remove',
        t('members.remove'),
        () => setMemberToRemove(member),
        {
          icon: 'trash',
          variant: 'danger'
        }
      )
    ],
    [createMenuItem, handlePromote, handleSetOwner, handleRemoveGerant, openDeclareGerant, t]
  );

  return (
    <section className="flex flex-col gap-5">
      {(title || titleDescription) && (
        <div>
          {title && (
            <h3 className="text-2xl font-bold text-gray-900">
              {title} ({members.length})
            </h3>
          )}
          {titleDescription && (
            <p className={`text-sm text-gray-500 ${title ? 'mt-1' : ''}`}>
              {titleDescription}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <GenericLoading loading />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {members.map(member => (
            <CollaboratorCard
              key={member.accessId}
              member={member}
              onView={() => openMemberModal(member)}
              menu={
                <DropdownButton
                  width="w-64"
                  anchor="bottom"
                  items={createMenuItems(member)}
                />
              }
            />
          ))}
          <AddMemberCard onClick={openAddForm} />
        </div>
      )}

      <ConfirmationOverlay
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemove}
        title={t('members.removeTitle')}
        description={
          memberToRemove
            ? t('members.removeConfirm', { name: memberToRemove.displayName })
            : ''
        }
        confirmLabel={t('members.remove')}
        cancelLabel={t('members.cancel')}
        variant="danger"
      />
    </section>
  );
};
